/**
 * 本文テキストを「Markdownリンク `[テキスト](href)`」と「生URL（https?://...）」に分解する
 * 共通トークナイザ。
 *
 * クローラー取り込み本文では、リンクが `[テキスト](href)` 形式で保存される（Issue #2900）。
 * 一方、Markdown化されていない既存テキストの生URLも従来どおりリンク化する必要があるため、
 * 両方を統一的に扱えるトークン列へ変換する。
 *
 * セキュリティ: href は http(s) スキームのみリンク化する（`javascript:` 等は弾く）。
 */

export type LinkToken =
  | { type: 'text'; value: string }
  // raw: true は生URL（表示テキスト === href）、false は Markdownリンク由来
  | { type: 'link'; text: string; href: string; raw: boolean };

// 安全なスキーム（http/https）のみリンク化する。二重防御として描画側でも利用可能。
export const SAFE_HREF_REGEX = /^https?:\/\//i;

// Markdownリンク: [表示テキスト](http(s)://...)
// href は http(s) のみ許可。テキストは `]` を含まない範囲。
// href は空白を含まない範囲とし、URL 内に現れる丸括弧（例: Wikipedia の
// `..._(programming_language)`）を許容するため、対応の取れた `(...)` を 1 段だけ
// バランスマッチする。これにより `[text](https://.../Foo_(bar))` を取りこぼさず、
// 末尾の `)` を Markdown の閉じ括弧として正しく扱える。
const MARKDOWN_LINK_REGEX = /\[([^\]]+?)\]\((https?:\/\/(?:[^\s()]|\([^\s()]*\))+)\)/g;

// 生URL
const RAW_URL_REGEX = /https?:\/\/[^\s]+/g;

// Markdownリンク外のテキストを、生URLとプレーンテキストに分解して push する
const pushRawUrlTokens = (tokens: LinkToken[], text: string): void => {
  if (text === '') {
    return;
  }
  const regex = new RegExp(RAW_URL_REGEX);
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    const url = match[0];
    tokens.push({ type: 'link', text: url, href: url, raw: true });
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.slice(lastIndex) });
  }
};

/**
 * テキストをリンクトークン列へ分解する。
 * Markdownリンクを先に処理し、その外側のテキストに対して生URLの自動リンク化を行う
 * （href 内のURLを二重リンク化しない）。
 */
export const tokenizeLinks = (input: string): LinkToken[] => {
  const tokens: LinkToken[] = [];
  if (!input) {
    return tokens;
  }
  const regex = new RegExp(MARKDOWN_LINK_REGEX);
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(input)) !== null) {
    if (match.index > lastIndex) {
      pushRawUrlTokens(tokens, input.slice(lastIndex, match.index));
    }
    const [, text, href] = match;
    if (SAFE_HREF_REGEX.test(href)) {
      tokens.push({ type: 'link', text, href, raw: false });
    } else {
      // 理論上 regex で http(s) のみだが、安全でないスキームはテキストとして残す（二重防御）
      tokens.push({ type: 'text', value: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < input.length) {
    pushRawUrlTokens(tokens, input.slice(lastIndex));
  }
  return tokens;
};

/** テキストにリンク（Markdownリンク or 生URL）が含まれるかどうか */
export const hasLinks = (input: string): boolean =>
  tokenizeLinks(input).some((token) => token.type === 'link');
